import {
  FONT,
  ROW_WINDOW,
  alpha,
  baseText,
  categoryAxis,
  compactScreen,
  grid,
  halo,
  horizontalAxes,
  labelGutter,
  legend,
  nameColumn,
  palette,
  rowZoom,
  rowsHeight,
  timeZoom,
  tooltip,
  valueAxis,
} from '../../components/chartTheme'
import { isRtl } from '../../lib/i18n'
import { day, decimal, num, pct, rateValue } from './reportFormat'
import { badHue, goodHue, hue, inkOn, metricHue, statusHues, tint } from './reportPalette'

/**
 * The Reports page's chart shapes, built on the panel's shared marks (`chartTheme`), after the
 * user's two reference boards (2026-10-12): light marks on white, a figure printed where it is
 * read, and one chart shape per question.
 *
 * Each has a table beside it carrying the same numbers (`ChartPanel`), so no value is readable
 * only by hovering:
 *
 *   outcomeColumnsOption     clicked against reported, period by period (grouped columns)
 *   campaignTrendOption      the same two rates as lines, campaign by campaign
 *   riskMatrixOption         where each department sits on clicking × reporting (bubbles)
 *   standingOption           which departments stand apart from the organisation — and is it more
 *                            than chance? (each one a dot on its own 95% interval)
 *   statusShareOption        where does each programme's audience stand? (100% stacked bars)
 *   histogramOption          how are scores spread? (a mean hides a split workforce)
 *   monthlyOption            how many incident reports, and how many held up?
 *   rateBarsOption           a rate per row, as a lollipop (weakest topics, riskiest templates)
 *   countBarsOption          a count per row, as a lollipop (incident categories)
 *   dumbbellOption           what moved between the baseline and the follow-up, topic by topic?
 *   quarterlyCoverageOption  is coverage rising quarter on quarter?
 *   ringOption               the parts of one whole, as a ring (the centre figure is HTML)
 *
 * BIG DATA. None of them grows without end: a ranking past `ROW_WINDOW` rows is a window with a
 * scrollbar, and a series past its window shows the latest points with a slider reaching back.
 * Every builder that windows takes `{ all: true }` to draw the whole thing at full height
 * instead — the panel's "Show all".
 *
 * COLOUR. Every indicator draws in its own softened hue from `reportPalette` — click coral,
 * reporting teal, assessments lavender, training blue, policies amber — and outcome states take
 * the status set. Text stays in ink; only marks wear colour, and fills stay light.
 */

const escape = (text) =>
  String(text ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c])

/* The chart host is dir="ltr"; each tooltip line takes its direction from its own text. The
   first line is the heading (the campaign, the month, the department), set a size up; the rest
   are aligned rows. */
const lines = (...rows) => {
  const shown = rows.filter(Boolean)
  return `<div style="display:grid;gap:7px;min-width:190px;line-height:1.35">${shown
    .map((row, index) =>
      index === 0
        ? `<div dir="auto" style="font-size:13.5px;font-weight:700;color:${palette().strong};padding-bottom:2px">${row}</div>`
        : `<div dir="auto">${row}</div>`,
    )
    .join('')}</div>`
}

/* A tooltip's figure row: the swatch and the label at the reading start in the quieter ink, the
   value at the far end in the strong one, so a column of figures lines up. */
const figure = (label, value, swatch) =>
  `<span style="display:flex;align-items:center;justify-content:space-between;gap:18px">` +
  `<span style="display:inline-flex;align-items:center;gap:8px;color:${palette().muted}">` +
  `${swatch ? `<span style="flex:none;width:9px;height:9px;border-radius:50%;background:${swatch}"></span>` : ''}${escape(label)}</span>` +
  `<b style="color:${palette().strong};font-variant-numeric:tabular-nums">${value}</b></span>`

/* A vertical wash: the hue at `top` alpha at the top, `bottom` at the baseline. */
const wash = (color, top, bottom = 0) => ({
  type: 'linear',
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: tint(color, top) },
    { offset: 1, color: tint(color, bottom) },
  ],
})

/* A status tone from the status set; anything else from the panel's palette. */
const toneColour = (tone, colours) => statusHues()[tone] ?? colours[tone] ?? statusHues().absent

/* A figure printed on the canvas, in ink. */
const valueLabel = (extra = {}) => ({
  show: true,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: palette().strong,
  ...halo,
  ...extra,
})

const percentAxis = (extra = {}) => ({
  ...valueAxis,
  min: 0,
  max: 100,
  axisLabel: { ...baseText, formatter: (value) => `${value}%` },
  ...extra,
})

/* FSI…PDI around a figure printed on the canvas. The host is dir="ltr" and `%` is a weak
   character, so beside an Arabic word it detached from its digits — "%المنظمة 12". */
const isolate = (text) => `⁨${text}⁩`

/* A rate axis that fits the data: from zero, to the next ten above the highest reading. */
const fittedMax = (values, headroom = 4) => {
  const top = Math.max(0, ...values.filter((value) => value != null))
  return Math.min(100, Math.max(10, Math.ceil((top + headroom) / 10) * 10))
}

/* A percent scale whose top is one of its own ticks — "0 20 40 60" never ends in a stray "70"
   crowding the last tick. Picks the step that wastes the least headroom in at most `parts` steps. */
const evenScale = (top, parts = 4) => {
  let best = null
  for (const step of [5, 10, 20, 25, 50]) {
    const steps = Math.ceil(top / step)
    if (steps > parts) continue
    const max = Math.min(100, steps * step)
    if (!best || max < best.max) best = { max, interval: step }
  }
  return best ?? { max: 100, interval: 25 }
}

/* A row of a horizontal chart: on a phone the names wrap to two lines and need the air. */
const rowPx = (row) => (compactScreen() ? row + 8 : row)

/* The dashed reference line every ranking is read against — the organisation's rate. */
const referenceLine = (value, label, axis = 'xAxis') => ({
  symbol: 'none',
  silent: true,
  label: {
    position: axis === 'xAxis' ? 'start' : 'insideEndTop',
    distance: 6,
    formatter: `${label ? `${label} ` : ''}${isolate(`${decimal(value)}%`)}`,
    fontFamily: FONT,
    fontSize: 11,
    fontWeight: 600,
    color: palette().muted,
    backgroundColor: palette().surface,
    padding: [2, 6],
    borderRadius: 4,
  },
  lineStyle: { color: palette().muted, type: [4, 4], width: 1, opacity: 0.6 },
  data: [{ [axis]: Number(value) }],
})

/**
 * Click and report rates side by side, one pair of rounded columns per period (a campaign, or a
 * month), each with its figure on top — the reference board's "campaign outcomes". Past
 * `points` periods the latest show and a slider reaches back.
 *
 * @param {{ label: string, click: number|null, report: number|null, note?: string }[]} points
 * @param {{ click: string, report: string, window?: number }} labels
 */
export function outcomeColumnsOption(points, labels) {
  const colours = palette()
  const clickInk = metricHue('clickRate')
  const reportInk = metricHue('reportRate')
  const zoom = timeZoom(points.length, { points: labels.window ?? 12 })
  const many = points.length > 8
  const last = points.length - 1
  /* The pair's two figures lean away from each other — the click figure out to its bar's outer
     side, the report figure to its own — so a short column's figure never sits over the taller
     column beside it. */
  const column = (name, key, color, lean) => ({
    name,
    type: 'bar',
    barMaxWidth: 22,
    barGap: '12%',
    barCategoryGap: many ? '30%' : '38%',
    itemStyle: { color, borderRadius: [5, 5, 0, 0] },
    emphasis: { itemStyle: { color: tint(color, 0.85) } },
    label: valueLabel({
      position: 'top',
      distance: 4,
      fontSize: 12,
      color: colours.strong,
      align: lean,
      offset: [lean === 'right' ? 6 : -6, 0],
      /* Every pair carries its figures while there is room (eight periods); past that only the
         latest pair does, and the tooltip and the table carry the rest. */
      formatter: ({ value, dataIndex }) => (value == null || (many && dataIndex !== last) ? '' : `${decimal(value)}%`),
    }),
    labelLayout: { hideOverlap: true },
    data: points.map((point) => point[key]),
  })
  return {
    tooltip: {
      ...tooltip('axis'),
      axisPointer: { type: 'shadow', shadowStyle: { color: alpha(colours.muted, 0.06) } },
      formatter: (params) => {
        const point = points[params[0]?.dataIndex ?? 0]
        if (!point) return ''
        return lines(
          `<b>${escape(point.label)}</b>`,
          point.note ? `<span style="color:${colours.muted}">${escape(point.note)}</span>` : '',
          figure(labels.click, pct(point.click), clickInk),
          figure(labels.report, pct(point.report), reportInk),
        )
      },
    },
    legend: { ...legend, data: [labels.click, labels.report] },
    /* The far side keeps room for the latest pair's figures. */
    grid: { ...grid, top: 24, right: grid.right + 18, bottom: zoom.bottom },
    dataZoom: zoom.dataZoom,
    xAxis: categoryAxis(points.map((point) => point.short ?? point.label), { maxLabel: 14 }),
    yAxis: percentAxis({ max: fittedMax(points.flatMap((point) => [point.click, point.report]), 8), splitNumber: 4 }),
    series: [column(labels.click, 'click', clickInk, 'right'), column(labels.report, 'report', reportInk, 'left')],
  }
}

/**
 * Per-campaign click and report rates as two lines, in launch order — for a long run of
 * campaigns, where columns would be a forest. Straight segments (the line between two campaigns
 * is not a measurement), a light wash under each, and the figure at the last point only.
 *
 * @param {object[]} points  `{ name, launchedAt, difficulty, delivered, clickRate, reportRate }`
 * @param {{ click: string, report: string, delivered: string, difficulty: object,
 *           tones?: { click?: string, report?: string } }} labels  `tones` are palette keys, for a
 *   page whose own stage colours differ (the phishing report draws "clicked" in amber)
 */
export function campaignTrendOption(points, labels) {
  const colours = palette()
  const clickInk = labels.tones?.click ? toneColour(labels.tones.click, colours) : metricHue('clickRate')
  const reportInk = labels.tones?.report ? toneColour(labels.tones.report, colours) : metricHue('reportRate')
  const many = points.length > 16
  const zoom = timeZoom(points.length)
  const last = points.length - 1
  const series = (name, key, color, symbol) => ({
    name,
    type: 'line',
    smooth: false,
    connectNulls: false,
    symbol,
    symbolSize: many ? 6 : 8,
    showSymbol: !many,
    lineStyle: { width: 2, color },
    areaStyle: { color: wash(color, 0.16) },
    itemStyle: { color: colours.surface, borderColor: color, borderWidth: 2 },
    emphasis: { focus: 'series' },
    label: valueLabel({
      position: 'top',
      distance: 8,
      color,
      formatter: ({ dataIndex, value }) => (dataIndex === last && value != null ? `${decimal(value)}%` : ''),
    }),
    data: points.map((point) => rateValue(point[key])),
  })

  return {
    tooltip: {
      ...tooltip('axis'),
      formatter: (params) => {
        const point = points[params[0]?.dataIndex ?? 0]
        if (!point) return ''
        const difficulty = labels.difficulty?.[point.difficulty]
        return lines(
          `<b>${escape(point.name)}</b>`,
          `<span style="color:${colours.muted}">${day(point.launchedAt)}${difficulty ? ` · ${escape(difficulty)}` : ''} · ${escape(labels.delivered)} ${num(point.delivered)}</span>`,
          figure(labels.click, pct(rateValue(point.clickRate)), clickInk),
          figure(labels.report, pct(rateValue(point.reportRate)), reportInk),
        )
      },
    },
    legend: { ...legend, data: [labels.click, labels.report] },
    grid: { ...grid, top: 28, right: 40, bottom: zoom.bottom },
    dataZoom: zoom.dataZoom,
    xAxis: {
      ...categoryAxis(points.map((point) => day(point.launchedAt))),
      boundaryGap: points.length < 2,
    },
    yAxis: percentAxis({
      max: fittedMax(points.flatMap((point) => [rateValue(point.clickRate), rateValue(point.reportRate)])),
      splitNumber: 4,
    }),
    series: [
      series(labels.click, 'clickRate', clickInk, 'circle'),
      series(labels.report, 'reportRate', reportInk, 'diamond'),
    ],
  }
}

/**
 * THE DEPARTMENT RISK MATRIX — each department a bubble placed by its click rate (across) and its
 * report rate (up), sized by its people, on dashed lines at the organisation's two rates. The
 * corner with fewer clicks and more reports is washed teal ("resilient"); the one with more clicks
 * and fewer reports coral ("needs attention"); each bubble wears its corner's colour.
 *
 * Names are printed beside the bubbles where they fit, largest first, and the rest are in the
 * tooltip and the table. A click opens the department (`params.data.id`).
 *
 * @param {{ id: number, name: string, x: number, y: number, size: number }[]} rows
 * @param {{ xOrg: number|null, yOrg: number|null, xLabel: string, yLabel: string, sizeLabel: string,
 *           resilientLabel: string, attentionLabel: string, organisationLabel: string }} opts
 */
export function riskMatrixOption(rows, opts) {
  const colours = palette()
  const xs = rows.map((row) => row.x)
  const ys = rows.map((row) => row.y)
  const xOrg = opts.xOrg ?? (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)
  const yOrg = opts.yOrg ?? (ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 0)
  const xMax = fittedMax([...xs, xOrg], 3)
  const yMax = fittedMax([...ys, yOrg], 6)
  const biggest = Math.max(1, ...rows.map((row) => row.size))
  const good = goodHue()
  const bad = badHue()
  const corner = (row) =>
    row.x <= xOrg && row.y >= yOrg ? good : row.x > xOrg && row.y < yOrg ? bad : row.x > xOrg ? hue('lavender') : hue('amber')
  /* Largest first, so where two names would collide the bigger department keeps its label. */
  const ordered = [...rows].sort((a, b) => b.size - a.size)
  /* NAMES ONLY WHERE THEY MATTER. Forty departments' names on one plot are a smudge: the three
     furthest into "needs attention" and the most resilient one are named, the rest are in the
     tooltip and the table. */
  const risk = (row) => (row.x - xOrg) / Math.max(1, xMax) - (row.y - yOrg) / Math.max(1, yMax)
  const byRisk = [...rows].sort((a, b) => risk(b) - risk(a))
  /* Two names never share a spot: a second department is named only when it sits clear of the
     first on the plot, otherwise the next one along is. */
  const apart = (a, b) => Math.hypot((a.x - b.x) / Math.max(1, xMax), (a.y - b.y) / Math.max(1, yMax)) > 0.14
  const picked = []
  const room = compactScreen() ? 1 : 2
  for (const row of byRisk.slice(0, 6)) {
    if (picked.length === room) break
    if (picked.every((item) => apart(item, row))) picked.push(row)
  }
  const resilient = byRisk[byRisk.length - 1]
  if (!compactScreen() && resilient && !picked.includes(resilient) && picked.every((item) => apart(item, resilient)))
    picked.push(resilient)
  const named = new Set(picked.map((row) => row.id))
  const quadrantLabel = (text, color, position) => ({
    show: true,
    position,
    distance: 8,
    color,
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 700,
    formatter: text,
  })

  return {
    tooltip: {
      ...tooltip('item'),
      formatter: ({ data }) => {
        const row = rows.find((item) => item.id === data?.id)
        if (!row) return ''
        return lines(
          `<b>${escape(row.name)}</b>`,
          figure(opts.xLabel, pct(row.x), metricHue('clickRate')),
          figure(opts.yLabel, pct(row.y), metricHue('reportRate')),
          `<span style="color:${colours.muted}">${escape(opts.sizeLabel)} ${num(row.size)}</span>`,
        )
      },
    },
    grid: { top: 36, left: 12, right: 24, bottom: 40, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' },
    xAxis: {
      ...valueAxis,
      min: 0,
      max: xMax,
      name: opts.xLabel,
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { ...baseText, color: colours.muted },
      splitLine: { show: false },
      axisLine: { show: true, lineStyle: { color: colours.grid } },
      axisLabel: { ...baseText, formatter: (value) => `${value}%` },
    },
    yAxis: {
      ...valueAxis,
      min: 0,
      max: yMax,
      /* Across the top rather than rotated: an Arabic label turned on its side reads as noise. */
      name: opts.yLabel,
      nameLocation: 'end',
      nameGap: 12,
      nameTextStyle: { ...baseText, color: colours.muted, align: 'left' },
      splitLine: { show: false },
      axisLine: { show: true, lineStyle: { color: colours.grid } },
      axisLabel: { ...baseText, formatter: (value) => `${value}%` },
    },
    series: [
      {
        type: 'scatter',
        data: ordered.map((row) => {
          const color = corner(row)
          return {
            id: row.id,
            name: row.name,
            value: [row.x, row.y],
            /* Small dots, not balloons: a headcount spread of 2–300 people drawn 8–22px apart
               keeps forty departments readable on one plot. */
            symbolSize: 8 + Math.sqrt(row.size / biggest) * 14,
            itemStyle: { color: tint(color, 0.55), borderColor: color, borderWidth: 1.5 },
            label: { show: named.has(row.id), position: row.x > xMax * 0.72 ? 'left' : 'top' },
          }
        }),
        label: {
          show: true,
          position: 'top',
          distance: 6,
          padding: [2, 6],
          borderRadius: 6,
          backgroundColor: colours.surface,
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          color: colours.strong,
          formatter: ({ data }) => isolate(data.name),
        },
        labelLayout: { hideOverlap: true, moveOverlap: 'shiftY' },
        emphasis: { scale: 1.08, label: { fontWeight: 700 } },
        markArea: {
          silent: true,
          data: [
            /* Each quadrant's name in a corner its departments leave empty: "resilient" by the centre
               cross (its outer corner holds the most resilient one and its name), "needs attention"
               at the far bottom. */
            [
              { xAxis: 0, yAxis: yOrg, itemStyle: { color: tint(good, 0.07) }, label: quadrantLabel(opts.resilientLabel, good, 'insideTopRight') },
              { xAxis: xOrg, yAxis: yMax },
            ],
            [
              { xAxis: xOrg, yAxis: 0, itemStyle: { color: tint(bad, 0.07) }, label: quadrantLabel(opts.attentionLabel, bad, 'insideBottomRight') },
              { xAxis: xMax, yAxis: yOrg },
            ],
          ],
        },
        markLine: {
          symbol: 'none',
          silent: true,
          label: { show: false },
          lineStyle: { color: colours.muted, type: [4, 4], width: 1, opacity: 0.55 },
          data: [{ xAxis: xOrg }, { yAxis: yOrg }],
        },
      },
    ],
  }
}

const STANDING_LABEL_WIDTH = 168

/**
 * WHERE EACH DEPARTMENT STANDS — a dot for its rate on a pale bar for its 95% interval, against a
 * dashed line for the organisation's rate.
 *
 * A department is flagged when its whole interval sits on the worse side of the line, so the
 * reader can see why one department of twelve people at 30% is not flagged while one of four
 * hundred at 22% is. Behind is said by shape as well as colour — a coral triangle — and every
 * other department is a dot in the indicator's hue. Position, not length, carries the value, so
 * the scale fits the data instead of starting at zero.
 *
 * @param {{ id: number, name: string, rate: object }[]} rows  `rate` is a RateDto with low/high
 * @param {{ organisation: number|null, valueLabel: string, organisationLabel: string,
 *           behindLabel: string, departmentsLabel: string, intervalLabel: string,
 *           hue?: string, all?: boolean }} opts
 */
export function standingOption(rows, opts) {
  const colours = palette()
  const { organisation, all = false } = opts
  const names = rows.map((row) => row.name)
  const lows = rows.map((row) => Number(row.rate.low ?? row.rate.value))
  const highs = rows.map((row) => Number(row.rate.high ?? row.rate.value))
  const span = [...lows, ...highs, organisation ?? lows[0]].filter((value) => Number.isFinite(value))
  const min = Math.max(0, Math.floor((Math.min(...span) - 2) / 10) * 10)
  const max = Math.min(100, Math.ceil((Math.max(...span) + 2) / 10) * 10)
  const column = nameColumn(STANDING_LABEL_WIDTH)
  const axes = horizontalAxes(names, column)
  const zoom = all ? undefined : rowZoom(rows.length)
  const point = (row, index) => ({ id: row.id, value: [Number(row.rate.value), index] })
  const behindInk = badHue()
  const dotInk = opts.hue && opts.hue !== behindInk ? opts.hue : hue('lavender')
  const track = tint(dotInk, 0.16)

  return {
    tooltip: {
      ...tooltip('item'),
      formatter: ({ data, seriesType }) => {
        if (seriesType !== 'scatter') return ''
        const row = rows.find((item) => item.id === data.id)
        if (!row) return ''
        return lines(
          `<b>${escape(row.name)}</b>`,
          figure(opts.valueLabel, pct(row.rate.value)),
          `<span style="color:${colours.muted}">${escape(opts.intervalLabel)} ${decimal(row.rate.low)}–${decimal(row.rate.high)}% · ${num(row.rate.numerator)} / ${num(row.rate.denominator)}</span>`,
          organisation == null ? '' : `<span style="color:${colours.muted}">${escape(opts.organisationLabel)} ${pct(organisation)}</span>`,
          row.rate.behind ? `<b style="color:${colours.behind}">▲ ${escape(opts.behindLabel)}</b>` : '',
        )
      },
    },
    legend: {
      ...legend,
      data: [
        { name: opts.departmentsLabel, icon: 'circle', itemStyle: { color: dotInk } },
        { name: opts.behindLabel, icon: 'triangle', itemStyle: { color: behindInk } },
      ],
    },
    grid: { top: 28, bottom: 40, ...labelGutter(column.labelWidth + (zoom ? 14 : 0)) },
    dataZoom: zoom,
    xAxis: {
      ...axes.xAxis,
      min,
      max,
      splitNumber: compactScreen() ? 3 : 5,
      axisLabel: { ...baseText, hideOverlap: true, formatter: (value) => `${value}%` },
    },
    yAxis: axes.yAxis,
    series: [
      /* The interval: an invisible run from the scale's start to the low end, then the bar from
         low to high. Stacked bars are the one mark ECharts windows with a category axis. */
      { type: 'bar', stack: 'interval', silent: true, barWidth: 8, itemStyle: { color: 'transparent' }, data: lows },
      {
        type: 'bar',
        stack: 'interval',
        silent: true,
        barWidth: 8,
        itemStyle: { color: track, borderRadius: 4 },
        data: highs.map((high, index) => Math.max(0.4, high - lows[index])),
      },
      {
        name: opts.departmentsLabel,
        type: 'scatter',
        symbol: 'circle',
        symbolSize: 11,
        itemStyle: { color: dotInk, borderColor: colours.surface, borderWidth: 2 },
        emphasis: { scale: 1.3 },
        data: rows.flatMap((row, index) => (row.rate.behind ? [] : [point(row, index)])),
        markLine: organisation == null ? undefined : referenceLine(organisation, opts.organisationLabel),
      },
      {
        name: opts.behindLabel,
        type: 'scatter',
        symbol: 'triangle',
        symbolSize: 13,
        itemStyle: { color: behindInk, borderColor: colours.surface, borderWidth: 2 },
        emphasis: { scale: 1.3 },
        data: rows.flatMap((row, index) => (row.rate.behind ? [point(row, index)] : [])),
      },
    ],
  }
}

/** The height a standing chart of `count` rows takes — windowed, or whole. */
export const standingHeight = (count, all = false) =>
  all ? Math.max(200, count * rowPx(30) + 72) : rowsHeight(count, { row: rowPx(30) })

const SHARE_LABEL_WIDTH = 168

/* Round the two outer ends of a 100% bar: the first segment's start, the last one's end. */
function segmentRadius(position, count, r) {
  if (count === 1) return r
  if (position === 0) return isRtl ? [0, r, r, 0] : [r, 0, 0, r]
  if (position === count - 1) return isRtl ? [r, 0, 0, r] : [0, r, r, 0]
  return 0
}

/**
 * Mutually exclusive states as one 100% bar per row — each row read against its own audience —
 * with each share printed inside its segment where it fits and, past the bar's end, the row's
 * headline share in bold (the reference board's "completion by department"). Past `ROW_WINDOW`
 * rows it scrolls in a window.
 *
 * @param {{ name: string, counts: Record<string, number> }[]} rows
 * @param {{ key: string, label: string, tone: string }[]} segments  in reading order; `tone` is
 *   a status key
 * @param {{ all?: boolean, total?: string[] }} [opts]  `total`: the segment keys whose share is
 *   printed past the bar (completion = passed + failed)
 */
export function statusShareOption(rows, segments, { all = false, total } = {}) {
  const colours = palette()
  const totals = rows.map((row) => segments.reduce((sum, segment) => sum + (row.counts[segment.key] ?? 0), 0))
  const share = (count, whole) => (whole ? Math.round((count / whole) * 1000) / 10 : 0)
  const headline = total
    ? rows.map((row, index) => share(total.reduce((sum, key) => sum + (row.counts[key] ?? 0), 0), totals[index]))
    : null
  const column = nameColumn(SHARE_LABEL_WIDTH)
  const axes = horizontalAxes(
    rows.map((row) => row.name),
    { max: 100, ...column },
  )
  let zoom = all ? undefined : rowZoom(rows.length)
  if (zoom && headline) zoom = zoom.map((item) => ({ ...item, yAxisIndex: [0, 1] }))
  /* The far side holds the bold share past each bar, and the scrollbar when the rows window. */
  const far = (headline ? 58 : 8) + (zoom ? 16 : 0)

  return {
    tooltip: {
      ...tooltip('axis'),
      axisPointer: { type: 'shadow', shadowStyle: { color: alpha(colours.muted, 0.06) } },
      formatter: (params) => {
        const index = params[0]?.dataIndex ?? 0
        const row = rows[index]
        return lines(
          `<b>${escape(row.name)}</b>`,
          ...segments.map((segment) =>
            figure(
              segment.label,
              `${num(row.counts[segment.key] ?? 0)} · ${pct(share(row.counts[segment.key] ?? 0, totals[index]))}`,
              toneColour(segment.tone, colours),
            ),
          ),
        )
      },
    },
    legend: { ...legend, data: segments.map((segment) => segment.label) },
    grid: { top: 8, bottom: 44, ...labelGutter(column.labelWidth, far) },
    dataZoom: zoom,
    xAxis: {
      ...axes.xAxis,
      splitNumber: compactScreen() ? 2 : 5,
      splitLine: { show: false },
      axisLabel: { ...baseText, hideOverlap: true, formatter: (value) => `${value}%` },
    },
    yAxis: headline
      ? [
          axes.yAxis,
          {
            ...axes.yAxis,
            position: isRtl ? 'left' : 'right',
            data: headline.map((value) => `${decimal(value)}%`),
            axisLabel: { ...baseText, color: colours.strong, fontWeight: 700, width: undefined, formatter: undefined, margin: zoom ? 22 : 8 },
          },
        ]
      : axes.yAxis,
    series: segments.map((segment, position) => {
      const fill = toneColour(segment.tone, colours)
      return {
        name: segment.label,
        type: 'bar',
        stack: 'share',
        barMaxWidth: 22,
        itemStyle: {
          color: fill,
          borderColor: colours.surface,
          borderWidth: 1.5,
          borderRadius: segmentRadius(position, segments.length, 5),
        },
        label: valueLabel({
          position: 'inside',
          color: inkOn(fill),
          textBorderWidth: 0,
          fontSize: 11.5,
          fontWeight: 700,
          /* Only where it fits; the tooltip and the table carry every figure. */
          formatter: ({ value }) => (value >= (compactScreen() ? 24 : 12) ? `${decimal(value)}%` : ''),
        }),
        emphasis: { focus: 'series' },
        data: rows.map((row, index) => share(row.counts[segment.key] ?? 0, totals[index])),
      }
    }),
  }
}

/** The height a 100% share chart of `count` rows takes — windowed, or whole. */
export const shareHeight = (count, all = false) =>
  all ? Math.max(200, count * rowPx(36) + 64) : rowsHeight(count, { row: rowPx(36), chrome: 64 })

/** Ten-point bands, the way the API counts them: [0,10) … [90,100]. */
export const BANDS = Array.from({ length: 10 }, (_, index) => (index === 9 ? '90–100' : `${index * 10}–${index * 10 + 9}`))

/**
 * How many completers scored in each band, as soft lavender columns that sit almost edge to
 * edge (a histogram, not a ranking); the tallest band deeper, with its count. The pass mark
 * differs between assessments, so no band is a verdict.
 */
export function histogramOption(distribution, { people, scoreLabel, hue: barHue = metricHue('assessmentPass') }) {
  const colours = palette()
  const total = distribution.reduce((sum, count) => sum + count, 0)
  const top = Math.max(...distribution)
  return {
    tooltip: {
      ...tooltip('item'),
      formatter: ({ dataIndex, value }) =>
        lines(`<b>${BANDS[dataIndex]}</b>`, figure(people, `${num(value)} · ${pct(total ? Math.round((value / total) * 1000) / 10 : null)}`, barHue)),
    },
    grid: { ...grid, top: 24, bottom: scoreLabel ? 28 : 8 },
    xAxis: {
      ...categoryAxis(BANDS),
      axisLine: { show: true, lineStyle: { color: colours.grid } },
      axisLabel: { ...baseText, interval: 0, fontSize: compactScreen() ? 10 : 12 },
      ...(scoreLabel ? { name: scoreLabel, nameLocation: 'middle', nameGap: 26, nameTextStyle: { ...baseText, color: colours.muted } } : null),
    },
    yAxis: { ...valueAxis, minInterval: 1, splitNumber: 4 },
    series: [
      {
        type: 'bar',
        barCategoryGap: '6%',
        itemStyle: { borderRadius: [3, 3, 0, 0] },
        label: valueLabel({
          position: 'top',
          color: colours.strong,
          formatter: ({ value }) => (value && value === top ? num(value) : ''),
        }),
        data: distribution.map((value) => ({
          value,
          itemStyle: { color: value === top && top > 0 ? barHue : tint(barHue, 0.55) },
        })),
      },
    ],
  }
}

/**
 * Incident reports per month, stacked by how they ended up. Past a year the latest twelve
 * months show and a slider reaches back.
 *
 * @param {{ month: string, valid: number, false: number, open: number }[]} months
 * @param {{ key: string, label: string, tone: string }[]} segments
 */
export function monthlyOption(months, segments) {
  const colours = palette()
  const zoom = timeZoom(months.length, { points: 12 })
  return {
    tooltip: {
      ...tooltip('axis'),
      axisPointer: { type: 'shadow', shadowStyle: { color: alpha(colours.muted, 0.06) } },
      formatter: (params) => {
        const month = months[params[0]?.dataIndex ?? 0]
        return lines(
          `<b>${escape(month.month)}</b>`,
          ...segments.map((segment) => figure(segment.label, num(month[segment.key]), toneColour(segment.tone, colours))),
        )
      },
    },
    legend: { ...legend, data: segments.map((segment) => segment.label) },
    grid: { ...grid, top: 24, bottom: zoom.bottom },
    dataZoom: zoom.dataZoom,
    xAxis: categoryAxis(months.map((month) => month.month)),
    yAxis: { ...valueAxis, minInterval: 1, splitNumber: 4 },
    series: segments.map((segment, position) => ({
      name: segment.label,
      type: 'bar',
      stack: 'reports',
      barMaxWidth: 34,
      itemStyle: {
        color: toneColour(segment.tone, colours),
        borderColor: colours.surface,
        borderWidth: 1.5,
        borderRadius: position === segments.length - 1 ? [5, 5, 0, 0] : 0,
      },
      emphasis: { focus: 'series' },
      ...(position === segments.length - 1
        ? {
            label: valueLabel({
              position: 'top',
              color: colours.strong,
              formatter: ({ dataIndex }) => {
                const month = months[dataIndex]
                const sum = segments.reduce((total, item) => total + (month[item.key] ?? 0), 0)
                return sum ? num(sum) : ''
              },
            }),
          }
        : null),
      data: months.map((month) => month[segment.key] ?? 0),
    })),
  }
}

const RATE_LABEL_WIDTH = 176

/* A lollipop: a thin stem from the axis to the value, a dot at the value, the figure past the
   dot. Shared by the rate and the count rankings. */
function lollipop(rows, values, { valueLabel: name, color, format, axis, all, average, averageLabel, describe }) {
  const colours = palette()
  const zoom = all ? undefined : rowZoom(rows.length)
  const column = nameColumn(RATE_LABEL_WIDTH)
  const axes = horizontalAxes(
    rows.map((row) => row.name),
    { ...column, max: axis?.max },
  )
  return {
    tooltip: {
      ...tooltip('item'),
      formatter: ({ data }) => {
        const row = rows[data?.row]
        if (!row) return ''
        return lines(`<b>${escape(row.name)}</b>`, figure(name, format(values[data.row]), color), ...(describe?.(row) ?? []))
      },
    },
    /* The far side keeps room for the figure printed past a dot near the end. */
    grid: { top: average == null ? 12 : 28, bottom: 24, ...labelGutter(column.labelWidth + (zoom ? 14 : 0), 48) },
    dataZoom: zoom,
    xAxis: {
      ...axes.xAxis,
      /* A count's scale is left to the axis, with headroom past the longest stem for its figure. */
      ...(axis?.percent
        ? evenScale(axis.max ?? 100, compactScreen() ? 2 : 4)
        : { max: undefined, boundaryGap: [0, 0.16], minInterval: 1, splitNumber: compactScreen() ? 2 : 4 }),
      axisLabel: { ...baseText, hideOverlap: true, formatter: axis?.percent ? (value) => `${value}%` : (value) => num(value) },
    },
    yAxis: axes.yAxis,
    series: [
      {
        type: 'bar',
        silent: true,
        barWidth: 3,
        itemStyle: { color: tint(color, 0.4), borderRadius: 2 },
        data: values,
        ...(average == null ? null : { markLine: referenceLine(average, averageLabel) }),
      },
      {
        name,
        type: 'scatter',
        symbol: 'circle',
        symbolSize: 13,
        itemStyle: { color, borderColor: colours.surface, borderWidth: 2 },
        emphasis: { scale: 1.25 },
        label: valueLabel({
          position: isRtl ? 'left' : 'right',
          distance: 8,
          formatter: ({ value }) => (value?.[0] == null ? '' : format(value[0])),
        }),
        /* Each dot carries its row, so the tooltip finds it with empty rows left out. */
        data: values.flatMap((value, index) => (value == null ? [] : [{ value: [value, index], row: index }])),
      },
    ],
  }
}

/**
 * One rate per row as a lollipop, in the caller's order (weakest or riskiest first), against a
 * dashed organisation line. Past `ROW_WINDOW` rows it scrolls in a window.
 *
 * @param {{ name: string, rate: object, note?: string }[]} rows  `rate` is a RateDto
 * @param {{ valueLabel: string, average?: number|null, averageLabel?: string, all?: boolean,
 *           hue?: string }} opts
 */
export function rateBarsOption(rows, { valueLabel: name, average = null, averageLabel = '', all = false, hue: color = hue('blue') }) {
  const colours = palette()
  const values = rows.map((row) => rateValue(row.rate))
  return lollipop(rows, values, {
    valueLabel: name,
    color,
    format: (value) => `${decimal(value)}%`,
    /* Fitted to the data, as the reference board's is: six topics between 30% and 50% drawn on
       0–100% leave half the plot empty and the differences squeezed. */
    axis: { max: fittedMax([...values, average], 8), percent: true },
    all,
    average,
    averageLabel,
    describe: (row) => [
      row.rate?.denominator != null ? `<span style="color:${colours.muted}">${num(row.rate.numerator)} / ${num(row.rate.denominator)}</span>` : '',
      row.note ? escape(row.note) : '',
    ],
  })
}

/** The height a rate-bars chart of `count` rows takes — windowed, or whole. */
export const rateBarsHeight = (count, all = false) =>
  all ? Math.max(200, count * rowPx(34) + 56) : rowsHeight(count, { row: rowPx(34), chrome: 56 })

/**
 * A count per row as a lollipop, largest first — incident categories. A window past
 * `ROW_WINDOW` rows.
 *
 * @param {{ name: string, value: number, note?: string }[]} rows
 */
export function countBarsOption(rows, { valueLabel: name, all = false, hue: color = hue('teal') }) {
  const colours = palette()
  const top = Math.max(1, ...rows.map((row) => row.value))
  return lollipop(
    rows,
    rows.map((row) => row.value),
    {
      valueLabel: name,
      color,
      format: num,
      axis: { max: top },
      all,
      describe: (row) => [row.note ? `<span style="color:${colours.muted}">${escape(row.note)}</span>` : ''],
    },
  )
}

/**
 * Baseline and follow-up for each topic, for the people who took both: a pale ring where the
 * baseline stood, a deep dot where the follow-up stands, both figures printed beside them, and
 * the bar between them is the change. Rows arrive ordered (largest fall first). Position carries
 * the value, so the scale is fitted to the data.
 *
 * @param {{ name: string, baseline: number, followUp: number }[]} rows
 * @param {{ baselineLabel: string, followUpLabel: string, changeLabel: string, all?: boolean }} opts
 */
export function dumbbellOption(rows, { baselineLabel, followUpLabel, changeLabel, all = false }) {
  const colours = palette()
  const before = hue('lavenderSoft')
  const after = metricHue('assessmentPass')
  const zoom = all ? undefined : rowZoom(rows.length)
  const values = rows.flatMap((row) => [row.baseline, row.followUp])
  const min = Math.max(0, Math.floor((Math.min(...values) - 8) / 10) * 10)
  const max = Math.min(100, Math.ceil((Math.max(...values) + 8) / 10) * 10)
  const column = nameColumn(RATE_LABEL_WIDTH)
  const axes = horizontalAxes(
    rows.map((row) => row.name),
    column,
  )
  const lows = rows.map((row) => Math.min(row.baseline, row.followUp))
  /* Each figure on the outside of its own dot: the lower reading toward the scale's start. */
  const side = (row, which) => {
    const lower = which === 'baseline' ? row.baseline <= row.followUp : row.followUp < row.baseline
    const start = isRtl ? 'right' : 'left'
    const end = isRtl ? 'left' : 'right'
    return lower ? start : end
  }
  const dots = (name, key, color, ring) => ({
    name,
    type: 'scatter',
    symbol: 'circle',
    symbolSize: 12,
    itemStyle: ring ? { color: before, borderColor: before, borderWidth: 2 } : { color, borderColor: colours.surface, borderWidth: 2 },
    data: rows.map((row, index) => ({
      value: [row[key], index],
      label: valueLabel({
        position: side(row, key),
        distance: 8,
        color: ring ? colours.muted : colours.strong,
        formatter: () => `${decimal(row[key])}%`,
      }),
    })),
  })
  return {
    tooltip: {
      ...tooltip('axis'),
      axisPointer: { type: 'shadow', shadowStyle: { color: alpha(colours.muted, 0.06) } },
      formatter: (params) => {
        const row = rows[params[0]?.dataIndex ?? 0]
        if (!row) return ''
        const delta = Math.round((row.followUp - row.baseline) * 10) / 10
        return lines(
          `<b>${escape(row.name)}</b>`,
          figure(baselineLabel, pct(row.baseline), before),
          figure(followUpLabel, pct(row.followUp), after),
          figure(changeLabel, `${delta > 0 ? '+' : ''}${decimal(delta)}`),
        )
      },
    },
    legend: {
      ...legend,
      top: 0,
      bottom: undefined,
      data: [
        { name: baselineLabel, icon: 'circle', itemStyle: { color: before } },
        { name: followUpLabel, icon: 'circle', itemStyle: { color: after } },
      ],
    },
    grid: { top: 36, bottom: 24, ...labelGutter(column.labelWidth + (zoom ? 14 : 0), 16) },
    dataZoom: zoom,
    xAxis: {
      ...axes.xAxis,
      min,
      max,
      splitNumber: compactScreen() ? 3 : 5,
      axisLabel: { ...baseText, hideOverlap: true, formatter: (value) => `${value}%` },
    },
    yAxis: axes.yAxis,
    series: [
      { type: 'bar', stack: 'move', silent: true, barWidth: 3, itemStyle: { color: 'transparent' }, data: lows },
      {
        type: 'bar',
        stack: 'move',
        silent: true,
        barWidth: 3,
        itemStyle: { borderRadius: 2 },
        data: rows.map((row, index) => ({
          value: Math.max(0.3, Math.max(row.baseline, row.followUp) - lows[index]),
          itemStyle: { color: row.followUp >= row.baseline ? tint(after, 0.45) : tint(badHue(), 0.5) },
        })),
      },
      dots(baselineLabel, 'baseline', before, true),
      dots(followUpLabel, 'followUp', after, false),
    ],
  }
}

/**
 * Training completion and policy acknowledgement at each quarter's end, from the nightly
 * snapshots — the reference board's "completion trend": a line per measure with a light wash
 * under it and its figure at every reading (there are only ever a handful of quarters). Straight
 * segments: a quarter-end is a reading, not a curve.
 *
 * @param {{ quarter: string, trainingCompletion: object, policyAcknowledgement: object }[]} points
 * @param {{ training: string, policy: string }} labels
 */
export function quarterlyCoverageOption(points, labels) {
  const colours = palette()
  const trainingInk = metricHue('trainingCompletion')
  const policyInk = metricHue('policyAcknowledgement')
  const labelled = points.length <= 10
  /* At each quarter the higher reading's figure goes above its line and the lower one's below,
     so where the lines meet the two figures part instead of printing over each other. */
  const above = (point, key, other) => (rateValue(point[key]) ?? -1) >= (rateValue(point[other]) ?? -1)
  const series = (name, key, color, other) => ({
    name,
    type: 'line',
    smooth: false,
    connectNulls: false,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: { width: 2.5, color },
    areaStyle: { color: wash(color, 0.14) },
    itemStyle: { color: colours.surface, borderColor: color, borderWidth: 2 },
    emphasis: { focus: 'series' },
    label: valueLabel({
      show: labelled,
      distance: 7,
      color: colours.strong,
      formatter: ({ value }) => (value == null ? '' : `${decimal(value)}%`),
    }),
    data: points.map((point) => ({
      value: rateValue(point[key]),
      label: { position: above(point, key, other) ? 'top' : 'bottom' },
    })),
  })
  return {
    tooltip: {
      ...tooltip('axis'),
      formatter: (params) => {
        const point = points[params[0]?.dataIndex ?? 0]
        if (!point) return ''
        return lines(
          `<b>${escape(point.quarter)}</b>`,
          figure(labels.training, pct(rateValue(point.trainingCompletion)), trainingInk),
          figure(labels.policy, pct(rateValue(point.policyAcknowledgement)), policyInk),
          `<span style="color:${colours.muted}">${day(point.snapshotDate)}</span>`,
        )
      },
    },
    legend: { ...legend, data: [labels.training, labels.policy] },
    grid: { ...grid, top: 28, right: 24, left: 16 },
    xAxis: { ...categoryAxis(points.map((point) => point.quarter)), boundaryGap: false },
    yAxis: percentAxis({ interval: 25 }),
    series: [
      series(labels.training, 'trainingCompletion', trainingInk, 'policyAcknowledgement'),
      series(labels.policy, 'policyAcknowledgement', policyInk, 'trainingCompletion'),
    ],
  }
}

/**
 * The parts of one whole as a ring with rounded, separated segments. The figure in the middle
 * and the key beside it are HTML (`Ring` in ReportBits), so they stay sharp and selectable.
 *
 * @param {{ key: string, label: string, value: number, color: string }[]} parts
 */
export function ringOption(parts) {
  const colours = palette()
  const total = parts.reduce((sum, part) => sum + (part.value || 0), 0)
  return {
    tooltip: {
      ...tooltip('item'),
      formatter: ({ data }) =>
        lines(figure(data.name, `${num(data.value)} · ${pct(total ? Math.round((data.value / total) * 1000) / 10 : null)}`, data.itemStyle.color)),
    },
    series: [
      {
        type: 'pie',
        radius: ['68%', '88%'],
        padAngle: parts.filter((part) => part.value > 0).length > 1 ? 2 : 0,
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: { scale: true, scaleSize: 4 },
        itemStyle: { borderRadius: 4, borderColor: colours.surface, borderWidth: 1 },
        data: parts
          .filter((part) => part.value > 0)
          .map((part) => ({ name: part.label, value: part.value, itemStyle: { color: part.color } })),
      },
    ],
  }
}

/** Rows a windowed ranking shows before its scrollbar — re-exported for the panels' wording. */
export { ROW_WINDOW }
